import { useEffect, useMemo, useRef, useState } from "react";
import BidStatsPanel from "../components/bids/BidStatsPanel";
import {
  Plus,
  Eye,
  Trash2,
  Save,
  X,
  Edit2,
  Search,
  Zap,
  MoreVertical,
  ExternalLink,
  Download,
  FolderPlus,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../store/useAuthStore";
import { useBidStore } from "../store/useBidStore";
import LoaderSpinner from "../components/ui/LoaderSpinner";
import BidQuickViewDrawer from "../components/bids/BidQuickViewDrawer";
import toast from "react-hot-toast";
import JobAnalysisModal from "../components/bids/JobAnalysisModal";
import { Sparkles, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import type { JobCheckResult } from "../store/useBidStore";
import { useProjectStore } from "../store/useProjectStore";
import { axiosInstance } from "../lib/axios";

// Legacy fallback only — accounts are configuration-driven now (Freelance
// Accounts managed by Admin); this list is used until the accounts load.
const PLATFORM_OPTIONS = ["Irfan Baig", "Nadeem", "Farah", "Rizwan"];
// Job types are configurable too; these are the defaults until config loads.
const DEFAULT_JOB_TYPES = ["New Job", "Invitation", "Directly Awarded"];
const DIRECT_TYPE = "Directly Awarded";
const STATUS_OPTIONS = [
  "Bid Submitted",
  "Awarded Directly",
  "In Negotiation",
  "Successfully Converted",
  "Declined / Not Accepted",
  "Hired Other Freelancer",
  "Job Deleted (Upwork)",
];

const DELETED_STATUS = "Job Deleted (Upwork)";
const WON_STATUSES = ["Successfully Converted", "Awarded Directly"];

const STATUS_META: Record<
  string,
  { short: string; pill: string; funnel?: "won" | "active" | "wait" | "lost" }
> = {
  "Bid Submitted": {
    short: "Submitted",
    pill: "bg-sky-50 text-sky-700 ring-1 ring-sky-200",
    funnel: "active",
  },
  "Awarded Directly": {
    short: "Awarded",
    pill: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200",
    funnel: "won",
  },
  "In Negotiation": {
    short: "Negotiating",
    pill: "bg-amber-50 text-amber-700 ring-1 ring-amber-200",
    funnel: "wait",
  },
  "Successfully Converted": {
    short: "Won",
    pill: "bg-green-50 text-green-700 ring-1 ring-green-200",
    funnel: "won",
  },
  "Declined / Not Accepted": {
    short: "Declined",
    pill: "bg-red-50 text-red-700 ring-1 ring-red-200",
    funnel: "lost",
  },
  "Hired Other Freelancer": {
    short: "Lost",
    pill: "bg-slate-100 text-slate-600 ring-1 ring-slate-200",
    funnel: "lost",
  },
  "Job Deleted (Upwork)": {
    short: "Deleted",
    pill: "bg-zinc-100 text-zinc-600 ring-1 ring-zinc-300 line-through decoration-1",
    // No funnel bucket — a deleted job is void, not won/lost.
  },
};

const ACCOUNT_AVATAR: Record<string, string> = {
  "Irfan Baig": "bg-orange-100 text-orange-700",
  Nadeem: "bg-sky-100 text-sky-700",
  Farah: "bg-violet-100 text-violet-700",
  Rizwan: "bg-rose-100 text-rose-700",
};

const initialOf = (name = "") => name.trim().charAt(0).toUpperCase() || "?";

const StatusPill = ({ status }: { status?: string }) => {
  if (!status)
    return <span className="text-xs text-slate-400">—</span>;
  const meta = STATUS_META[status];
  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold ${
        meta?.pill || "bg-slate-100 text-slate-600 ring-1 ring-slate-200"
      }`}
      title={status}
    >
      {meta?.short || status}
    </span>
  );
};

interface DraftRow {
  tempId: string;
  jobTitle: string;
  jobLink: string;
  platformAccount: any;
  entryType: string;
  connectsUsed: number;
  bidAmount: string;
  bidType: string;
  boostUsed: boolean;
  boostConnects: number;
  boostPosition: number;
  bidStatus: string;
  projectRef: string;
}

const newDraft = (): DraftRow => ({
  tempId: `draft-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  jobTitle: "",
  jobLink: "",
  platformAccount: PLATFORM_OPTIONS[0],
  entryType: "New Job",
  connectsUsed: 0,
  bidAmount: "",
  bidType: "Fixed",
  boostUsed: false,
  boostConnects: 0,
  boostPosition: 1,
  bidStatus: "Bid Submitted",
  projectRef: "",
});

const BidsPage = () => {
  const { authUser } = useAuthStore();
  const {
    bids,
    accounts,
    fetchMyBids,
    fetchAccounts,
    createBid,
    updateBid,
    deleteBid,
    updateAccount,
    isFetching,
    checkJobUrl,
    analyzeJob,
  } = useBidStore();

  const [drafts, setDrafts] = useState<DraftRow[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<any>(null);

  const topScrollRef = useRef<HTMLDivElement | null>(null);
  const bottomScrollRef = useRef<HTMLDivElement | null>(null);
  const TABLE_MIN_WIDTH = 1280;

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedBid, setSelectedBid] = useState<any>(null);

  const [statusFilter, setStatusFilter] = useState("");
  const [platformFilter, setPlatformFilter] = useState("");
  const [boostFilter, setBoostFilter] = useState("");
  const [entryTypeFilter, setEntryTypeFilter] = useState("");
  const [query, setQuery] = useState("");
  // Date filtering: all | today | week | month | custom
  const [datePreset, setDatePreset] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [editingAccountId, setEditingAccountId] = useState<string | null>(null);
  const [accountTotalDraft, setAccountTotalDraft] = useState<string>("");

  const [draftChecks, setDraftChecks] = useState<Record<string, JobCheckResult & { checking?: boolean }>>({});

  const [aiModal, setAiModal] = useState<{ open: boolean; job: any; ai: any; url?: string }>({
    open: false, job: null, ai: null,
  });
  const [analyzing, setAnalyzing] = useState(false);
  const { projects, fetchProjects } = useProjectStore();
  const navigate = useNavigate();

useEffect(() => {
  if (authUser?._id) {
    fetchMyBids();
    fetchAccounts();
    fetchProjects();
  }

}, [authUser]);

  useEffect(() => {
    const timers: Record<string, any> = {};
    drafts.forEach((d) => {
      const url = d.jobLink?.trim();
      if (!url) return;
      const existing = draftChecks[d.tempId];
      if (existing && (existing as any)._url === url) return;

      timers[d.tempId] = setTimeout(async () => {
        setDraftChecks((p) => ({
          ...p,
          [d.tempId]: { status: "not_found", checking: true, _url: url } as any,
        }));
        try {
          const result = await checkJobUrl(url);
          setDraftChecks((p) => ({
            ...p,
            [d.tempId]: { ...result, checking: false, _url: url } as any,
          }));

          if (result.status === "not_found") {
            triggerAnalyze(url, d.tempId);
          }
        } catch {
          setDraftChecks((p) => ({
            ...p,
            [d.tempId]: { status: "invalid_url", checking: false, _url: url } as any,
          }));
        }
      }, 600);
    });
    return () => Object.values(timers).forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drafts.map((d) => d.tempId + "|" + d.jobLink).join(",")]);

const triggerAnalyze = async (url: string, draftId?: string) => {
    setAnalyzing(true);
    if (draftId) {
      setDraftChecks((p) => ({
        ...p,
        [draftId]: {
          ...(p[draftId] || { status: "not_found" }),
          checking: true,
          message: "Analyzing with AI…",
          _url: url,
        } as any,
      }));
    }
    try {
      const result = await analyzeJob(url);
      if (result.status === "completed" && result.job) {
        if (draftId) {
          setDraftChecks((p) => ({
            ...p,
            [draftId]: { ...result, checking: false, _url: url } as any,
          }));
        }
      } else if (draftId) {
        setDraftChecks((p) => ({
          ...p,
          [draftId]: {
            ...(p[draftId] || { status: "not_found" }),
            checking: false,
            message: "Analysis queued — refresh shortly",
            _url: url,
          } as any,
        }));
      }
    } catch {
      if (draftId) {
        setDraftChecks((p) => ({
          ...p,
          [draftId]: {
            ...(p[draftId] || { status: "not_found" }),
            checking: false,
            _url: url,
          } as any,
        }));
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const accountMap = useMemo(() => {
    const m: Record<string, any> = {};
    accounts.forEach((a) => (m[a.name] = a));
    return m;
  }, [accounts]);

  // Configuration-driven accounts: only ACTIVE accounts are offered for new
  // bids; filters list every account so historical bids stay reachable.
  const activeAccountNames = useMemo(() => {
    const names = accounts.filter((a: any) => a.isActive !== false).map((a: any) => a.name);
    return names.length ? names : PLATFORM_OPTIONS;
  }, [accounts]);
  const allAccountNames = useMemo(() => {
    const names = accounts.map((a: any) => a.name);
    return names.length ? names : PLATFORM_OPTIONS;
  }, [accounts]);
  // Account name → derived platform label (Platform is a property of the account).
  const platformOf = (name: string) => accountMap[name]?.platform?.name || "Upwork";
  // $ per connect for an account (account override → platform rate), for the
  // connect-cost preview shown BEFORE the BD confirms the bid.
  const rateFor = (name: string) => Number(accountMap[name]?.effectiveConnectRate) || 0;
  const costPreview = (name: string, connects: number) =>
    Math.round(connects * rateFor(name) * 100) / 100;

  // Configurable job types + statuses (Admin-managed) — defaults until loaded.
  const [jobTypes, setJobTypes] = useState<string[]>(DEFAULT_JOB_TYPES);
  const [statusOptionsCfg, setStatusOptionsCfg] = useState<string[]>(STATUS_OPTIONS);
  useEffect(() => {
    axiosInstance
      .get("/bids/config")
      .then(({ data }) => {
        const activeOf = (list: any[]) => (list || []).filter((t: any) => t.isActive !== false).map((t: any) => t.name);
        const types = activeOf(data.jobTypes);
        const statuses = activeOf(data.bidStatuses);
        if (types.length) setJobTypes(types);
        if (statuses.length) setStatusOptionsCfg(statuses);
      })
      .catch(() => { /* defaults stay */ });
  }, []);

  const dateBounds = useMemo(() => {
    const now = new Date();
    const sod = (d: any) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
    const eod = (d: any) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };
    if (datePreset === "today") return { from: sod(now), to: eod(now) };
    if (datePreset === "week") { const f = sod(now); f.setDate(f.getDate() - 6); return { from: f, to: eod(now) }; }
    if (datePreset === "month") { const f = sod(now); f.setDate(f.getDate() - 29); return { from: f, to: eod(now) }; }
    if (datePreset === "custom")
      return { from: fromDate ? sod(fromDate) : null, to: toDate ? eod(toDate) : null };
    return { from: null as Date | null, to: null as Date | null };
  }, [datePreset, fromDate, toDate]);

  const filtered = useMemo(() => {
    return bids.filter((b: any) => {
      if (statusFilter && b.bidStatus !== statusFilter) return false;
      if (platformFilter && b.platformAccount !== platformFilter) return false;
      if (boostFilter === "boosted" && !b.boostUsed) return false;
      if (boostFilter === "organic" && b.boostUsed) return false;
      if (entryTypeFilter === "Invitation" && b.entryType !== "Invitation") return false;
      if (entryTypeFilter === DIRECT_TYPE && b.entryType !== DIRECT_TYPE) return false;
      if (entryTypeFilter === "New Job" && ["Invitation", DIRECT_TYPE].includes(b.entryType)) return false;
      if (dateBounds.from || dateBounds.to) {
        const c = new Date(b.createdAt || b.submissionTimestamp || 0);
        if (dateBounds.from && c < dateBounds.from) return false;
        if (dateBounds.to && c > dateBounds.to) return false;
      }
      if (query) {
        const q = query.toLowerCase();
        if (
          !(
            b.jobTitle?.toLowerCase().includes(q) ||
            b.jobLink?.toLowerCase().includes(q) ||
            b.proposalLink?.toLowerCase().includes(q) ||
            b.notes?.toLowerCase().includes(q) ||
            b.platformAccount?.toLowerCase().includes(q)
          )
        )
          return false;
      }
      return true;
    });
  }, [bids, statusFilter, platformFilter, boostFilter, entryTypeFilter, query, dateBounds]);

  // Export the currently-filtered bids to a CSV file.
  const exportCSV = () => {
    const esc = (v: any) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const headers = [
      "Job Title", "Job Link", "Account", "Platform", "Job Type", "Type", "Base Connects", "Boost Connects", "Total Connects",
      "Connect Cost (USD)", "Amount (USD)", "Boost", "Status", "Submitted",
    ];
    const totalConnectsOf = (b: any) =>
      b.entryType === DIRECT_TYPE || b.connectsRefunded
        ? 0
        : (b.entryType === "Invitation" ? 0 : b.connectsUsed ?? 0) + (b.boostUsed ? b.boostConnects ?? 0 : 0);
    const lines = filtered.map((b: any) =>
      [
        b.jobTitle || "",
        b.jobLink || "",
        b.platformAccount || "",
        b.freelanceAccount?.platform?.name || platformOf(b.platformAccount),
        b.entryType || "New Job",
        b.bidType || "Fixed",
        b.entryType === "Invitation" || b.entryType === DIRECT_TYPE ? 0 : b.connectsUsed ?? 0,
        b.boostUsed ? b.boostConnects ?? 0 : 0,
        totalConnectsOf(b),
        Number(b.connectCost) || 0,
        b.bidAmount ?? 0,
        b.boostUsed ? "Boosted" : "Organic",
        b.bidStatus || "",
        b.submittedAt || b.createdAt ? new Date(b.submittedAt || b.createdAt).toLocaleString() : "",
      ].map(esc).join(",")
    );
    const csv = ["﻿" + headers.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bids-export-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Conversion metrics: Bid→Win and Bid→Negotiation→Win, with admin filters ──
  const isAdminView = !!authUser && !["Business Developer", "Client"].includes(authUser.role);
  const [convBd, setConvBd] = useState("");
  const [convAcct, setConvAcct] = useState("");
  const bdIdOf = (b: any) => String(b.createdBy?._id || b.createdBy || "");
  const convBdOptions = useMemo(() => {
    const m = new Map<string, string>();
    bids.forEach((b: any) => { const id = bdIdOf(b); if (id) m.set(id, b.createdBy?.fullName || "BD"); });
    return Array.from(m, ([id, name]) => ({ id, name }));
  }, [bids]);
  const conversion = useMemo(() => {
    const WON = ["Awarded Directly", "Successfully Converted"];
    const rows = bids.filter((b: any) => {
      if (convBd && bdIdOf(b) !== convBd) return false;
      if (convAcct && b.platformAccount !== convAcct) return false;
      return true;
    });
    // Directly Awarded opportunities never involved a traditional bid, so they
    // are EXCLUDED from the standard conversion rate (counted separately).
    const eligible = rows.filter((b: any) => b.entryType !== DIRECT_TYPE);
    const directAwarded = rows.length - eligible.length;
    const totalBids = eligible.length;
    const wonBids = eligible.filter((b: any) => WON.includes(b.bidStatus)).length;
    const negotiatedBids = eligible.filter((b: any) => b.wasNegotiated || b.bidStatus === "In Negotiation").length;
    const negWins = eligible.filter((b: any) => WON.includes(b.bidStatus) && b.wasNegotiated).length;
    const directWins = wonBids - negWins;
    // Connect spend across the filtered rows (base + boost, refunds excluded).
    const totalConnects = rows.reduce(
      (s: number, b: any) =>
        s + (b.totalConnects ?? (b.connectsRefunded || b.entryType === DIRECT_TYPE ? 0 : (b.connectsUsed || 0) + (b.boostUsed ? b.boostConnects || 0 : 0))),
      0
    );
    const connectCost = rows.reduce((s: number, b: any) => s + (Number(b.connectCost) || 0), 0);
    const pct = (n: number, d: number) => (d ? Math.round((n / d) * 100) : 0);
    return {
      totalBids, wonBids, negotiatedBids, negWins, directWins, directAwarded,
      totalConnects, connectCost: Math.round(connectCost * 100) / 100,
      bidToWin: pct(wonBids, totalBids),
      bidToNego: pct(negotiatedBids, totalBids),
      negoToWin: pct(negWins, negotiatedBids),
    };
  }, [bids, convBd, convAcct]);

  const analytics = useMemo(() => {
    const total = bids.length || 0;
    const boosted = bids.filter((b: any) => b.boostUsed).length;
    const boostPct = total ? Math.round((boosted / total) * 100) : 0;

    const funnel = { won: 0, active: 0, wait: 0, lost: 0 };
    bids.forEach((b: any) => {
      const f = STATUS_META[b.bidStatus]?.funnel;
      if (f) funnel[f] += 1;
    });

    const buckets = {
      small: { count: 0, won: 0, max: 1000 },
      medium: { count: 0, won: 0, max: 5000 },
      large: { count: 0, won: 0, max: Infinity },
    };
    bids.forEach((b: any) => {
      const amt = Number(b.bidAmount) || 0;
      const key =
        amt < buckets.small.max
          ? "small"
          : amt < buckets.medium.max
          ? "medium"
          : "large";
      buckets[key].count += 1;
      if (STATUS_META[b.bidStatus]?.funnel === "won") buckets[key].won += 1;
    });
    const winRate = (b: { count: number; won: number }) =>
      b.count ? Math.round((b.won / b.count) * 100) : 0;

    return {
      total,
      boostPct,
      funnel,
      buckets,
      winRate,
    };
  }, [bids]);

  const addDraftRow = () =>
    setDrafts((d) => [...d, { ...newDraft(), platformAccount: activeAccountNames[0] || "" }]);
  const removeDraftRow = (id: string) =>
    setDrafts((d) => d.filter((r) => r.tempId !== id));
  const updateDraft = (id: string, patch: Partial<DraftRow>) =>
    setDrafts((d) => d.map((r) => (r.tempId === id ? { ...r, ...patch } : r)));

  // Effective connects a row will consume: Directly Awarded = 0; Invitation =
  // boost connects only (a boosted invitation still spends connects); New Job =
  // base + boost.
  const effectiveOf = (entryType: string, connects: number, boostUsed: boolean, boostConnects: number) => {
    if (entryType === DIRECT_TYPE) return 0;
    const base = entryType === "Invitation" ? 0 : Number(connects) || 0;
    return base + (boostUsed ? Number(boostConnects) || 0 : 0);
  };

  const validateDraft = (r: DraftRow): string | null => {
    const isDirect = r.entryType === DIRECT_TYPE;
    if (!r.jobLink.trim() && !isDirect) return "Job Link is required";
    if (!r.platformAccount) return "Freelance account is required";
    if (r.bidAmount === "" || isNaN(Number(r.bidAmount)))
      return "Bid amount must be a number";
    if (Number(r.bidAmount) < 0) return "Bid amount cannot be negative";
    if (r.connectsUsed < 0) return "Connects cannot be negative";
    const acc = accountMap[r.platformAccount];
    if (acc && acc.isActive === false)
      return `${r.platformAccount} is deactivated — pick an active account`;
    const effective = effectiveOf(r.entryType, r.connectsUsed, r.boostUsed, r.boostConnects);
    if (acc) {
      const remaining = (acc.totalConnects || 0) - (acc.usedConnects || 0);
      if (effective > remaining)
        return `${r.platformAccount} has only ${remaining} connects available`;
    }
    const dupe = bids.find(
      (b: any) =>
        b.jobLink &&
        r.jobLink.trim() &&
        b.jobLink.trim().toLowerCase() === r.jobLink.trim().toLowerCase() &&
        b.platformAccount === r.platformAccount
    );
    if (dupe) return "A bid with this Job Link already exists on this account";
    return null;
  };

  const saveDraft = async (r: DraftRow) => {
    const err = validateDraft(r);
    if (err) return toast.error(err);
    try {
      const isInvite = r.entryType === "Invitation";
      const isDirect = r.entryType === DIRECT_TYPE;
      // Boost is available for New Job AND Invitation — never for Directly Awarded.
      const boostUsed = !isDirect && r.boostUsed;
      await createBid({
        jobTitle: r.jobTitle?.trim() || "",
        jobLink: r.jobLink,
        platformAccount: r.platformAccount,
        entryType: (r.entryType || "New Job") as any,
        connectsUsed: isInvite || isDirect ? 0 : r.connectsUsed,
        bidAmount: Number(r.bidAmount),
        bidType: (r.bidType || "Fixed") as "Fixed" | "Hourly",
        boostUsed,
        boostConnects: boostUsed ? r.boostConnects : 0,
        boostPosition: boostUsed ? r.boostPosition : null,
        bidStatus: isDirect && !WON_STATUSES.includes(r.bidStatus) ? "Awarded Directly" : r.bidStatus,
        projectRef: r.projectRef || undefined,
      });
      removeDraftRow(r.tempId);
      fetchAccounts();
    } catch {

    }
  };

  const startEdit = (bid: any) => {
    setEditingId(bid._id);
    setEditForm({
      jobTitle: bid.jobTitle || "",
      jobLink: bid.jobLink || "",
      platformAccount: bid.platformAccount,
      entryType: bid.entryType || "New Job",
      connectsUsed: bid.connectsUsed || 0,
      bidAmount: bid.bidAmount || "",
      bidType: bid.bidType || "Fixed",
      boostUsed: !!bid.boostUsed,
      boostConnects: bid.boostConnects || 0,
      boostPosition: bid.boostPosition || 1,
      bidStatus: bid.bidStatus || "Bid Submitted",
      connectsRefunded: !!bid.connectsRefunded,
      hasContact: !!bid.contactRef,
      clientName: bid.contactRef?.name || "",
      projectRef: bid.projectRef?._id || bid.projectRef || "",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(null);
  };

  const validateEdit = (orig: any, f: any): string | null => {
    const isDirect = f.entryType === DIRECT_TYPE;
    if (!f.jobLink?.toString().trim() && !isDirect) return "Job Link is required";
    if (f.bidAmount === "" || isNaN(Number(f.bidAmount)))
      return "Bid amount must be a number";
    if (Number(f.bidAmount) < 0) return "Bid amount cannot be negative";
    if (Number(f.connectsUsed) < 0) return "Connects cannot be negative";

    const sameAccount = orig.platformAccount === f.platformAccount;
    // Effective usage (base + boost) — refunded / Directly Awarded never count.
    const effOld = orig.connectsRefunded
      ? 0
      : effectiveOf(orig.entryType || "New Job", orig.connectsUsed || 0, !!orig.boostUsed, orig.boostConnects || 0);
    const effNew =
      f.bidStatus === DELETED_STATUS && f.connectsRefunded
        ? 0
        : effectiveOf(f.entryType || "New Job", Number(f.connectsUsed) || 0, !!f.boostUsed, Number(f.boostConnects) || 0);
    const acc = accountMap[f.platformAccount];
    if (acc) {
      const remaining = (acc.totalConnects || 0) - (acc.usedConnects || 0);
      const needed = sameAccount ? effNew - effOld : effNew;
      if (needed > remaining)
        return `${f.platformAccount} has only ${remaining} connects available`;
    }
    if (f.bidStatus === "In Negotiation" && !f.hasContact && !(f.clientName || "").trim())
      return "Client name is required to move this bid into Negotiation";
    return null;
  };

  const saveEdit = async (bid: any) => {
    const err = validateEdit(bid, editForm);
    if (err) return toast.error(err);
    try {
      const isInvite = editForm.entryType === "Invitation";
      const isDirect = editForm.entryType === DIRECT_TYPE;
      const boostUsed = !isDirect && !!editForm.boostUsed;
      const payload: any = {
        jobTitle: editForm.jobTitle?.trim() || "",
        jobLink: editForm.jobLink,
        platformAccount: editForm.platformAccount,
        entryType: (editForm.entryType || "New Job") as any,
        connectsUsed: isInvite || isDirect ? 0 : Number(editForm.connectsUsed) || 0,
        bidAmount: Number(editForm.bidAmount),
        bidType: (editForm.bidType || "Fixed") as "Fixed" | "Hourly",
        boostUsed,
        boostConnects: boostUsed ? Number(editForm.boostConnects) || 0 : 0,
        boostPosition: boostUsed ? Number(editForm.boostPosition) || 1 : null,
        bidStatus:
          isDirect && !WON_STATUSES.includes(editForm.bidStatus) ? "Awarded Directly" : editForm.bidStatus,
        connectsRefunded:
          editForm.bidStatus === DELETED_STATUS ? !!editForm.connectsRefunded : false,
        projectRef: editForm.projectRef || null,
      };
      if (editForm.bidStatus === "In Negotiation" && !editForm.hasContact) {
        payload.clientName = (editForm.clientName || "").trim();
      }
      await updateBid(bid._id, payload);
      cancelEdit();
      fetchAccounts();
    } catch {

    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this bid? Connects will be returned to the account."))
      return;
    await deleteBid(id);
    fetchAccounts();
  };

  if (isFetching && !bids.length) {
    return (
      <div className="py-16 flex justify-center">
        <LoaderSpinner size={48} />
      </div>
    );
  }

  const brandGradient =
    "bg-[linear-gradient(90deg,#03488A_0%,#00AAF1_100%)]";

  return (
    <div className={`min-h-screen bg-background ${authUser?.role === "Business Developer" ? "" : "pt-16"}`}>
      <div className="max-w-[1440px] mx-auto px-6 py-8 space-y-8">

        <div className="bg-surface-container-lowest rounded-xl border border-outline-variant card-shadow p-6 flex items-end justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-[26px] font-bold text-on-surface tracking-tight">
              My Bids
            </h1>
            <p className="text-[14px] text-on-surface-variant mt-1">
              Add bids directly in the table — connects deduct automatically.
            </p>
          </div>
        </div>

<div className="bg-white p-5 rounded-2xl border border-slate-200/60 shadow-[0px_4px_12px_rgba(25,28,29,0.03)] mb-4">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
              Conversion {isAdminView ? "Performance" : "— Your Performance"}
            </h3>
            {isAdminView && (
              <div className="flex flex-wrap gap-2">
                {convBdOptions.length > 1 && (
                  <select value={convBd} onChange={(e) => setConvBd(e.target.value)} className="px-2.5 py-1.5 border border-slate-200 rounded-lg text-[12px] bg-white focus:outline-none focus:ring-2 focus:ring-[#00AAF1]/40" title="Filter by BD">
                    <option value="">All BDs</option>
                    {convBdOptions.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                )}
                <select value={convAcct} onChange={(e) => setConvAcct(e.target.value)} className="px-2.5 py-1.5 border border-slate-200 rounded-lg text-[12px] bg-white focus:outline-none focus:ring-2 focus:ring-[#00AAF1]/40" title="Filter by freelance account">
                  <option value="">All accounts</option>
                  {allAccountNames.map((p) => <option key={p} value={p}>{p} — {platformOf(p)}</option>)}
                </select>
              </div>
            )}
          </div>
          {/* One compact container — Bids · Connects · Conversion, divided
              vertically — in place of the ten separate stat cards. The old
              Win Rate panel is gone: Bid → Win is one of the three rings now,
              and every figure comes from a single server-side definition so
              this cannot drift from the bid list or from Reports. */}
          <BidStatsPanel onOpenStatus={(status) => setStatusFilter(status)} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-4 gap-4">

          <div className="bg-white p-5 rounded-2xl border border-slate-200/60 shadow-[0px_4px_12px_rgba(25,28,29,0.03)] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                Connects Usage
              </h2>
              <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200 px-2 py-0.5 rounded-full uppercase tracking-wider flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                Live
              </span>
            </div>
            <div className="space-y-3.5 flex-1">
              {accounts.length === 0 && (
                <div className="text-xs text-slate-400 italic">
                  No accounts yet
                </div>
              )}
              {accounts.map((a) => {
                const used = a.usedConnects || 0;
                const total = a.totalConnects || 0;
                const pct = total ? (used / total) * 100 : 0;
                const isEditingAcc = editingAccountId === a._id;
                return (
                  <div key={a._id} className="space-y-1">
                    <div className="flex justify-between items-center text-xs">
                      <span className="font-bold text-slate-800 flex items-center gap-1.5">
                        <span
                          className={`w-5 h-5 rounded-full grid place-items-center text-[9px] font-bold ${
                            ACCOUNT_AVATAR[a.name] || "bg-slate-200 text-slate-700"
                          }`}
                        >
                          {initialOf(a.name)}
                        </span>
                        {a.name}
                      </span>
                      {!isEditingAcc ? (
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-[#03488A]">
                            {used} / {total}
                          </span>
                          <button
                            onClick={() => {
                              setEditingAccountId(a._id);
                              setAccountTotalDraft(String(total));
                            }}
                            className="text-slate-400 hover:text-[#03488A] transition"
                            title="Edit total connects"
                          >
                            <Edit2 size={12} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            min={0}
                            className="w-16 px-1.5 py-0.5 text-xs border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00AAF1]/30"
                            value={accountTotalDraft}
                            onChange={(e) =>
                              setAccountTotalDraft(e.target.value)
                            }
                          />
                          <button
                            onClick={async () => {
                              await updateAccount(
                                a._id,
                                Number(accountTotalDraft) || 0
                              );
                              setEditingAccountId(null);
                            }}
                            className="p-0.5 rounded bg-[#03488A] text-white hover:opacity-90"
                          >
                            <Save size={11} />
                          </button>
                          <button
                            onClick={() => setEditingAccountId(null)}
                            className="p-0.5 rounded border border-slate-200 text-slate-500 hover:bg-slate-50"
                          >
                            <X size={11} />
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className={brandGradient + " h-full transition-all"}
                        style={{ width: `${Math.min(100, pct)}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-white p-5 rounded-2xl border border-slate-200/60 shadow-[0px_4px_12px_rgba(25,28,29,0.03)] flex flex-col">
            <h3 className="text-xs font-bold text-slate-800 mb-4 uppercase tracking-wider">
              Boost Analysis
            </h3>
            <div className="flex items-center gap-5 flex-1">
              <div className="relative w-24 h-24 shrink-0">
                <svg className="w-full h-full -rotate-90" viewBox="0 0 80 80">
                  <circle
                    cx="40"
                    cy="40"
                    r="34"
                    fill="transparent"
                    stroke="#e2e8f0"
                    strokeWidth="7"
                  />
                  <circle
                    cx="40"
                    cy="40"
                    r="34"
                    fill="transparent"
                    stroke="url(#boostGrad)"
                    strokeWidth="7"
                    strokeLinecap="round"
                    strokeDasharray={2 * Math.PI * 34}
                    strokeDashoffset={
                      2 * Math.PI * 34 * (1 - analytics.boostPct / 100)
                    }
                    className="transition-all duration-700"
                  />
                  <defs>
                    <linearGradient id="boostGrad" x1="0" x2="1" y1="0" y2="0">
                      <stop offset="0%" stopColor="#03488A" />
                      <stop offset="100%" stopColor="#00AAF1" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-xl font-extrabold text-slate-800">
                    {analytics.boostPct}%
                  </span>
                  <span className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">
                    boosted
                  </span>
                </div>
              </div>
              <div className="space-y-2.5">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-sm bg-[#03488A]" />
                  <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                    Boosted
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-sm bg-slate-200" />
                  <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                    Organic
                  </span>
                </div>
                <div className="text-[10px] text-slate-400 pt-1">
                  {analytics.total} bids total
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-5 rounded-2xl border border-slate-200/60 shadow-[0px_4px_12px_rgba(25,28,29,0.03)] flex flex-col">
            <h3 className="text-xs font-bold text-slate-800 mb-4 uppercase tracking-wider">
              Win Rate by Bid Size
            </h3>
            <div className="space-y-3.5 flex-1 flex flex-col justify-center">
              {[
                {
                  label: "Small",
                  sub: "< $1K",
                  data: analytics.buckets.small,
                  bar: "bg-sky-300",
                },
                {
                  label: "Medium",
                  sub: "$1K – $5K",
                  data: analytics.buckets.medium,
                  bar: brandGradient,
                },
                {
                  label: "Large",
                  sub: "$5K+",
                  data: analytics.buckets.large,
                  bar: "bg-[#03488A]",
                },
              ].map((row) => {
                const rate = analytics.winRate(row.data);
                return (
                  <div key={row.label} className="space-y-1">
                    <div className="flex justify-between text-[10px] font-bold uppercase tracking-wider">
                      <span className="text-slate-800">
                        {row.label}{" "}
                        <span className="text-slate-400 font-medium normal-case tracking-normal">
                          {row.sub}
                        </span>
                      </span>
                      <span className="text-slate-500">
                        {rate}%{" "}
                        <span className="text-slate-400 font-medium">
                          ({row.data.count})
                        </span>
                      </span>
                    </div>
                    <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${row.bar} transition-all duration-500`}
                        style={{ width: `${rate}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-white p-5 rounded-2xl border border-slate-200/60 shadow-[0px_4px_12px_rgba(25,28,29,0.03)] flex flex-col">
            <h2 className="text-xs font-bold text-slate-800 mb-4 uppercase tracking-wider">
              Bid Funnel
            </h2>
            <div className="flex-1 flex flex-col justify-between gap-4">
              <div className="flex h-6 w-full bg-slate-100 rounded-lg overflow-hidden">
                {(() => {
                  const total =
                    analytics.funnel.won +
                      analytics.funnel.active +
                      analytics.funnel.wait +
                      analytics.funnel.lost || 1;
                  const seg = (v: number) => `${(v / total) * 100}%`;
                  return (
                    <>
                      <div
                        className="h-full bg-emerald-500 transition-all"
                        style={{ width: seg(analytics.funnel.won) }}
                      />
                      <div
                        className="h-full bg-[#03488A] transition-all"
                        style={{ width: seg(analytics.funnel.active) }}
                      />
                      <div
                        className="h-full bg-amber-400 transition-all"
                        style={{ width: seg(analytics.funnel.wait) }}
                      />
                      <div
                        className="h-full bg-red-500 transition-all"
                        style={{ width: seg(analytics.funnel.lost) }}
                      />
                    </>
                  );
                })()}
              </div>
              <div className="grid grid-cols-2 gap-x-2 gap-y-2">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-emerald-500" />
                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                    Won: {analytics.funnel.won}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-[#03488A]" />
                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                    Active: {analytics.funnel.active}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-amber-400" />
                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                    Wait: {analytics.funnel.wait}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-red-500" />
                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                    Lost: {analytics.funnel.lost}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h2 className="text-xl font-extrabold text-slate-800 tracking-tight">
              Recent Bidding Activity
            </h2>
            <div className="flex gap-2 items-center flex-wrap">
              <div className="relative w-full sm:w-auto">
                <Search
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  className="pl-9 pr-4 py-2 bg-white ring-1 ring-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00AAF1]/40 w-full sm:w-64"
                  placeholder="Search job, notes, account…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
              <select
                className="pl-3 pr-8 py-2 bg-white ring-1 ring-slate-200 rounded-lg text-sm font-medium text-slate-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#00AAF1]/40"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">Status</option>
                {statusOptionsCfg.map((s) => (
                  <option key={s} value={s}>
                    {STATUS_META[s]?.short || s}
                  </option>
                ))}
              </select>
              <select
                className="pl-3 pr-8 py-2 bg-white ring-1 ring-slate-200 rounded-lg text-sm font-medium text-slate-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#00AAF1]/40"
                value={platformFilter}
                onChange={(e) => setPlatformFilter(e.target.value)}
              >
                <option value="">Account</option>
                {allAccountNames.map((p) => (
                  <option key={p} value={p}>
                    {p} — {platformOf(p)}
                  </option>
                ))}
              </select>
              <select
                className="pl-3 pr-8 py-2 bg-white ring-1 ring-slate-200 rounded-lg text-sm font-medium text-slate-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#00AAF1]/40"
                value={boostFilter}
                onChange={(e) => setBoostFilter(e.target.value)}
              >
                <option value="">Boost</option>
                <option value="boosted">Boosted</option>
                <option value="organic">Organic</option>
              </select>
              <select
                className="pl-3 pr-8 py-2 bg-white ring-1 ring-slate-200 rounded-lg text-sm font-medium text-slate-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#00AAF1]/40"
                value={entryTypeFilter}
                onChange={(e) => setEntryTypeFilter(e.target.value)}
                title="Filter by bid type"
              >
                <option value="">Job Type</option>
                {jobTypes.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              <select
                className="pl-3 pr-8 py-2 bg-white ring-1 ring-slate-200 rounded-lg text-sm font-medium text-slate-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#00AAF1]/40"
                value={datePreset}
                onChange={(e) => setDatePreset(e.target.value)}
              >
                <option value="all">All time</option>
                <option value="today">Today</option>
                <option value="week">Last 7 Days</option>
                <option value="month">Last 30 Days</option>
                <option value="custom">Custom range</option>
              </select>
              {datePreset === "custom" && (
                <>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="px-3 py-2 bg-white ring-1 ring-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#00AAF1]/40 min-w-[140px] flex-1 sm:flex-none"
                    title="From date"
                  />
                  <span className="text-slate-400 text-sm">–</span>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="px-3 py-2 bg-white ring-1 ring-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#00AAF1]/40 min-w-[140px] flex-1 sm:flex-none"
                    title="To date"
                  />
                </>
              )}
              <button
                onClick={exportCSV}
                disabled={filtered.length === 0}
                className="px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-1.5 border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                title="Export filtered bids to CSV"
              >
                <Download size={16} /> Export CSV
              </button>
              <button
                onClick={addDraftRow}
                className={`${brandGradient} text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-1.5 shadow-sm hover:opacity-95 transition`}
              >
                <Plus size={16} /> New Bid
              </button>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200/60 shadow-[0px_12px_32px_rgba(25,28,29,0.04)] bg-white overflow-hidden">

            <div
              ref={topScrollRef}
              onScroll={(e) => {
                if (
                  bottomScrollRef.current &&
                  bottomScrollRef.current.scrollLeft !==
                    (e.target as HTMLDivElement).scrollLeft
                ) {
                  bottomScrollRef.current.scrollLeft = (
                    e.target as HTMLDivElement
                  ).scrollLeft;
                }
              }}
              className="cre-scroll overflow-x-auto border-b border-slate-100"
              style={{ height: 12 }}
              aria-hidden="true"
            >
              <div style={{ width: TABLE_MIN_WIDTH, height: 1 }} />
            </div>

            <div
              ref={bottomScrollRef}
              onScroll={(e) => {
                if (
                  topScrollRef.current &&
                  topScrollRef.current.scrollLeft !==
                    (e.target as HTMLDivElement).scrollLeft
                ) {
                  topScrollRef.current.scrollLeft = (
                    e.target as HTMLDivElement
                  ).scrollLeft;
                }
              }}
              className="cre-scroll overflow-x-auto"
            >
              <table
                className="w-full text-left border-collapse"
                style={{ minWidth: TABLE_MIN_WIDTH }}
              >
                <thead>
                  <tr className={`${brandGradient} text-white`}>
                    <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider">
                      Job Title
                    </th>
                         <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-center">
                      Link
                    </th>
                    <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider">
                      Account
                    </th>
                    <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-center">
                      Connects
                    </th>
                    <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-center">
                      Boost
                    </th>
                    <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider">
                      Status
                    </th>
                    {/* Project column hidden for now
                    <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider">
                      Project
                    </th>
                    */}
                    <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider">
                      Submitted
                    </th>

                    <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-right sticky right-0 z-20 bg-[#03488A] shadow-[-6px_0_8px_-4px_rgba(0,0,0,0.15)]">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">

                  {drafts.map((r) => {
                    const c = draftChecks[r.tempId];
                    return (
                      <tr
                        key={r.tempId}
                        className="bg-sky-50/40 hover:bg-sky-50/70 transition"
                      >

                        <td className="px-6 py-4 w-[240px] min-w-[220px] max-w-[240px]">
                          <div className="space-y-1.5">
                            <input
                              className="w-full px-2 py-1.5 border border-slate-200 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-[#00AAF1]/40 font-semibold"
                              placeholder="Job title (e.g. Shopify migration)"
                              value={r.jobTitle}
                              onChange={(e) =>
                                updateDraft(r.tempId, { jobTitle: e.target.value })
                              }
                            />
                            <input
                              className="w-full px-2 py-1.5 border border-slate-200 rounded-md text-[11px] text-slate-600 focus:outline-none focus:ring-2 focus:ring-[#00AAF1]/40"
                              placeholder="https://upwork.com/jobs/..."
                              value={r.jobLink}
                              onChange={(e) =>
                                updateDraft(r.tempId, { jobLink: e.target.value })
                              }
                            />
                            {(() => {
                              if (!r.jobLink?.trim()) return null;
                              if (!c || c.checking)
                                return (
                                  <span className="inline-flex items-center gap-1 text-[10px] text-slate-500">
                                    <Loader2 size={10} className="animate-spin" />
                                    {c?.message || "Checking…"}
                                  </span>
                                );
                              if (c.status === "invalid_url")
                                return (
                                  <span className="inline-flex items-center gap-1 text-[10px] text-amber-600">
                                    <AlertCircle size={10} /> Invalid Upwork URL
                                  </span>
                                );
                              if (c.status === "not_found")
                                return (
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="inline-flex items-center gap-1 text-[10px] text-slate-600">
                                      <AlertCircle size={10} /> New job
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        triggerAnalyze(r.jobLink, r.tempId)
                                      }
                                      className="text-[10px] font-bold text-violet-700 hover:underline inline-flex items-center gap-0.5"
                                    >
                                      <Sparkles size={10} /> Analyze
                                    </button>
                                  </div>
                                );
                              if (c.status === "matched") {
                                const decision = c.ai?.["Final Decision"];
                                return (
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700 font-semibold">
                                      <CheckCircle2 size={10} /> Matched
                                      {decision && (
                                        <span className="text-slate-500">
                                          • {decision}
                                        </span>
                                      )}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setAiModal({
                                          open: true,
                                          job: c.job,
                                          ai: c.ai,
                                          url: r.jobLink,
                                        })
                                      }
                                      className="text-[10px] font-bold text-[#03488A] hover:underline inline-flex items-center gap-0.5"
                                    >
                                      <Sparkles size={10} /> View AI
                                    </button>
                                  </div>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        </td>

                        {/* Link column (URL entered with the job title above) */}
                        <td className="px-6 py-4 text-center">
                          {r.jobLink ? (
                            <a
                              href={r.jobLink}
                              target="_blank"
                              rel="noreferrer noopener"
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-bold bg-slate-100 text-slate-700 hover:bg-slate-200 transition"
                              title={r.jobLink}
                            >
                              <ExternalLink size={12} />
                              Preview
                            </a>
                          ) : (
                            <span className="text-slate-300 text-[11px]">—</span>
                          )}
                        </td>

                        <td className="px-6 py-4">
                          <select
                            className="px-2 py-1.5 border border-slate-200 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-[#00AAF1]/40 bg-white"
                            value={r.platformAccount}
                            onChange={(e) =>
                              updateDraft(r.tempId, {
                                platformAccount: e.target.value,
                              })
                            }
                          >
                            {activeAccountNames.map((p) => {
                              const acc = accountMap[p];
                              const left = acc
                                ? (acc.totalConnects || 0) -
                                  (acc.usedConnects || 0)
                                : 0;
                              return (
                                <option key={p} value={p}>
                                  {p} — {platformOf(p)} ({left} left)
                                </option>
                              );
                            })}
                          </select>
                        </td>

                        <td className="px-6 py-4 text-center">
                          <select
                            className="mb-1.5 w-full px-2 py-1 border border-slate-200 rounded-md text-[11px] bg-white focus:outline-none focus:ring-2 focus:ring-[#00AAF1]/40"
                            value={r.entryType}
                            onChange={(e) => {
                              const t = e.target.value;
                              updateDraft(r.tempId, {
                                entryType: t,
                                // Invitation: no BASE connects (Boost still allowed).
                                // Directly Awarded: no connects, no boost, own status flow.
                                ...(t === "Invitation" ? { connectsUsed: 0 } : {}),
                                ...(t === DIRECT_TYPE
                                  ? { connectsUsed: 0, boostUsed: false, boostConnects: 0, bidStatus: "Awarded Directly" }
                                  : {}),
                              });
                            }}
                            title="Job Type — Invitation has 0 base connects (Boost still consumes); Directly Awarded needs no connects at all"
                          >
                            {jobTypes.map((t) => (
                              <option key={t} value={t}>{t === DIRECT_TYPE ? "Directly Awarded" : t}</option>
                            ))}
                          </select>
                          <input
                            type="number"
                            min={0}
                            placeholder="0"
                            disabled={r.entryType === "Invitation" || r.entryType === DIRECT_TYPE}
                            className="w-20 px-2 py-1.5 border border-slate-200 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-[#00AAF1]/40 disabled:bg-slate-100 disabled:text-slate-400"
                            value={r.entryType === "Invitation" || r.entryType === DIRECT_TYPE ? 0 : r.connectsUsed || ""}
                            onChange={(e) =>
                              updateDraft(r.tempId, {
                                connectsUsed: Number(e.target.value) || 0,
                              })
                            }
                          />
                          {/* Connect cost shown BEFORE the bid is confirmed */}
                          {(() => {
                            const eff = effectiveOf(r.entryType, r.connectsUsed, r.boostUsed, r.boostConnects);
                            if (!eff || !rateFor(r.platformAccount)) return null;
                            return (
                              <div className="mt-1 text-[10px] font-semibold text-rose-600" title={`${eff} connects × $${rateFor(r.platformAccount)}/connect`}>
                                ≈ ${costPreview(r.platformAccount, eff).toLocaleString()}
                              </div>
                            );
                          })()}
                        </td>

                        <td className="px-6 py-4">
                          <input
                            type="number"
                            min={0}
                            className="w-24 px-2 py-1.5 border border-slate-200 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-[#00AAF1]/40"
                            placeholder="USD"
                            value={r.bidAmount}
                            onChange={(e) =>
                              updateDraft(r.tempId, { bidAmount: e.target.value })
                            }
                          />
                        </td>

                        <td className="px-6 py-4">
                          <select
                            className="px-2 py-1.5 border border-slate-200 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-[#00AAF1]/40 bg-white"
                            value={r.bidType}
                            onChange={(e) =>
                              updateDraft(r.tempId, { bidType: e.target.value })
                            }
                          >
                            <option value="Fixed">Fixed</option>
                            <option value="Hourly">Hourly</option>
                          </select>
                        </td>

                        <td className="px-6 py-4 text-center">
                          <button
                            type="button"
                            disabled={r.entryType === DIRECT_TYPE}
                            onClick={() =>
                              updateDraft(r.tempId, { boostUsed: !r.boostUsed })
                            }
                            className="grid place-items-center mx-auto disabled:opacity-30 disabled:cursor-not-allowed"
                            title={
                              r.entryType === DIRECT_TYPE
                                ? "Directly Awarded jobs need no Boost — no bid is placed"
                                : r.boostUsed ? "Boosted" : "Organic"
                            }
                          >
                            <Zap
                              size={18}
                              className={
                                r.boostUsed
                                  ? "text-emerald-500 fill-emerald-500"
                                  : "text-slate-300"
                              }
                            />
                          </button>
                          {/* Boost details — connects + position + running total */}
                          {r.boostUsed && (
                            <div className="mt-2 flex flex-col gap-1.5 items-stretch min-w-[130px]">
                              <input
                                type="number"
                                min={0}
                                placeholder="Boost connects"
                                className="w-full px-2 py-1 border border-slate-200 rounded-md text-[11px] focus:outline-none focus:ring-2 focus:ring-[#00AAF1]/40"
                                value={r.boostConnects || ""}
                                onChange={(e) =>
                                  updateDraft(r.tempId, { boostConnects: Number(e.target.value) || 0 })
                                }
                              />
                              <select
                                className="w-full px-2 py-1 border border-slate-200 rounded-md text-[11px] bg-white focus:outline-none focus:ring-2 focus:ring-[#00AAF1]/40"
                                value={r.boostPosition}
                                onChange={(e) =>
                                  updateDraft(r.tempId, { boostPosition: Number(e.target.value) })
                                }
                              >
                                <option value={1}>1st Position</option>
                                <option value={2}>2nd Position</option>
                                <option value={3}>3rd Position</option>
                                <option value={4}>4th Position</option>
                              </select>
                              <div className="text-[10px] text-slate-500">
                                Total: <span className="font-bold text-emerald-600">{effectiveOf(r.entryType, r.connectsUsed, true, r.boostConnects)}</span>
                                {rateFor(r.platformAccount) > 0 && (
                                  <span className="ml-1 font-bold text-rose-600">
                                    (≈ ${costPreview(r.platformAccount, effectiveOf(r.entryType, r.connectsUsed, true, r.boostConnects)).toLocaleString()})
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </td>

                        <td className="px-6 py-4">
                          <select
                            className="px-2 py-1.5 border border-slate-200 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-[#00AAF1]/40 bg-white"
                            value={r.bidStatus}
                            onChange={(e) =>
                              updateDraft(r.tempId, { bidStatus: e.target.value })
                            }
                          >
                            {statusOptionsCfg.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </select>
                        </td>

                        {/* Project column hidden for now
                        <td className="px-6 py-4">
                          <select
                            className="px-2 py-1.5 border border-slate-200 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-[#00AAF1]/40 bg-white"
                            value={r.projectRef}
                            onChange={(e) =>
                              updateDraft(r.tempId, { projectRef: e.target.value })
                            }
                          >
                            <option value="">— None —</option>
                            {projects.map((p) => (
                              <option key={p._id} value={p._id}>
                                {p.name}
                              </option>
                            ))}
                          </select>
                        </td>
                        */}

                        <td className="px-6 py-4">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-sky-700 bg-sky-50 ring-1 ring-sky-200 px-2 py-0.5 rounded-full">
                            Draft
                          </span>
                        </td>

                        <td className="px-6 py-4 sticky right-0 z-10 bg-sky-50/95 shadow-[-6px_0_8px_-4px_rgba(15,23,42,0.08)]">
                          <div className="flex gap-1 justify-end">
                            <button
                              onClick={() => saveDraft(r)}
                              className={`${brandGradient} text-white p-1.5 rounded-md hover:opacity-90 shadow-sm`}
                              title="Save bid"
                            >
                              <Save size={14} />
                            </button>
                            <button
                              onClick={() => removeDraftRow(r.tempId)}
                              className="p-1.5 rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 hover:border-red-200 hover:text-red-500 transition"
                              title="Discard"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                  {filtered.map((b: any) => {
                    const isEdit = editingId === b._id;
                    const acctClasses =
                      ACCOUNT_AVATAR[b.platformAccount] ||
                      "bg-slate-100 text-slate-700";

                    const displayTitle = (() => {
                      if (b.jobTitle && b.jobTitle.trim()) return b.jobTitle.trim();
                      if (!b.jobLink) return "Untitled";
                      try {
                        const u = new URL(b.jobLink);
                        const seg = u.pathname.split("/").filter(Boolean);
                        return seg[seg.length - 1]?.replace(/[-_]/g, " ") ||
                          u.hostname;
                      } catch {
                        return b.jobLink;
                      }
                    })();
                    return (
                      <tr
                        key={b._id}
                        className={`group hover:bg-slate-50/70 transition ${
                          isEdit ? "bg-amber-50/40" : ""
                        }`}
                      >
                        <td className="px-6 py-4 w-[240px] min-w-[220px] max-w-[240px]">
                          {isEdit ? (
                            <div className="space-y-1.5">
                              <input
                                className="w-full px-2 py-1.5 border border-slate-200 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-[#00AAF1]/40 font-semibold"
                                placeholder="Job title"
                                value={editForm.jobTitle || ""}
                                onChange={(e) =>
                                  setEditForm({
                                    ...editForm,
                                    jobTitle: e.target.value,
                                  })
                                }
                              />
                              <input
                                className="w-full px-2 py-1.5 border border-slate-200 rounded-md text-[11px] text-slate-600 focus:outline-none focus:ring-2 focus:ring-[#00AAF1]/40"
                                placeholder="https://upwork.com/jobs/..."
                                value={editForm.jobLink}
                                onChange={(e) =>
                                  setEditForm({
                                    ...editForm,
                                    jobLink: e.target.value,
                                  })
                                }
                              />
                            </div>
                          ) : (
                            <div className="flex flex-col gap-0.5">
                              <span
                                className="font-semibold text-slate-800 leading-snug whitespace-normal break-words"
                                title={displayTitle}
                              >
                                {displayTitle}
                              </span>
                              {b.jobLink && (
                                <span
                                  className="text-[11px] text-slate-400 font-mono truncate"
                                  title={b.jobLink}
                                >
                                  {b.jobLink.replace(/^https?:\/\//, "")}
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                                             <td className="px-6 py-4 text-center">
                          {b.jobLink ? (
                            <a
                              href={b.jobLink}
                              target="_blank"
                              rel="noreferrer noopener"
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-bold bg-[#03488A] text-white hover:bg-[#0058c3] transition shadow-sm"
                              title={b.jobLink}
                            >
                              <ExternalLink size={12} />
                              Open
                            </a>
                          ) : (
                            <span className="text-slate-300 text-[11px]">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {isEdit ? (
                            <select
                              className="px-2 py-1.5 border border-slate-200 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-[#00AAF1]/40"
                              value={editForm.platformAccount}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  platformAccount: e.target.value,
                                })
                              }
                            >
                              {(allAccountNames.includes(editForm.platformAccount)
                                ? allAccountNames
                                : [editForm.platformAccount, ...allAccountNames]
                              ).map((p) => (
                                <option key={p} value={p}>
                                  {p} — {platformOf(p)}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <div className="flex items-center gap-2">
                              <div
                                className={`w-7 h-7 rounded-full grid place-items-center text-[11px] font-bold ${acctClasses}`}
                              >
                                {initialOf(b.platformAccount)}
                              </div>
                              <div className="flex flex-col leading-tight">
                                <span className="text-sm text-slate-700 font-medium">
                                  {b.platformAccount}
                                </span>
                                {/* Admins see who placed the bid + the linked client */}
                                {authUser?.role !== "Business Developer" && b.createdBy?.fullName && (
                                  <span className="text-[11px] text-slate-400">
                                    BD: {b.createdBy.fullName}
                                  </span>
                                )}
                                {b.contactRef?.name && (
                                  <span className="text-[11px] text-emerald-600 font-medium">
                                    {b.contactRef.name}
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </td>

                        <td className="px-6 py-4 text-center">
                          {isEdit ? (
                            <div className="flex flex-col gap-1">
                              <select
                                className="w-full px-2 py-1 border border-slate-200 rounded-md text-[11px] bg-white focus:outline-none focus:ring-2 focus:ring-[#00AAF1]/40"
                                value={editForm.entryType}
                                onChange={(e) => {
                                  const t = e.target.value;
                                  setEditForm({
                                    ...editForm,
                                    entryType: t,
                                    ...(t === "Invitation" ? { connectsUsed: 0 } : {}),
                                    ...(t === DIRECT_TYPE
                                      ? { connectsUsed: 0, boostUsed: false, boostConnects: 0, bidStatus: "Awarded Directly" }
                                      : {}),
                                  });
                                }}
                              >
                                {(jobTypes.includes(editForm.entryType)
                                  ? jobTypes
                                  : [editForm.entryType, ...jobTypes]
                                ).map((t) => (
                                  <option key={t} value={t}>{t}</option>
                                ))}
                              </select>
                              <input
                                type="number"
                                disabled={editForm.entryType === "Invitation" || editForm.entryType === DIRECT_TYPE}
                                className="w-20 px-2 py-1.5 border border-slate-200 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-[#00AAF1]/40 disabled:bg-slate-100 disabled:text-slate-400"
                                value={editForm.entryType === "Invitation" || editForm.entryType === DIRECT_TYPE ? 0 : editForm.connectsUsed}
                                onChange={(e) =>
                                  setEditForm({
                                    ...editForm,
                                    connectsUsed: e.target.value,
                                  })
                                }
                              />
                              {(() => {
                                const eff = effectiveOf(editForm.entryType, Number(editForm.connectsUsed) || 0, !!editForm.boostUsed, Number(editForm.boostConnects) || 0);
                                if (!eff || !rateFor(editForm.platformAccount)) return null;
                                return (
                                  <span className="text-[10px] font-semibold text-rose-600" title={`${eff} connects × $${rateFor(editForm.platformAccount)}/connect`}>
                                    ≈ ${costPreview(editForm.platformAccount, eff).toLocaleString()}
                                  </span>
                                );
                              })()}
                            </div>
                          ) : b.entryType === DIRECT_TYPE ? (
                            <span className="bg-teal-100 px-2.5 py-1 rounded-md text-[11px] font-bold text-teal-700" title="Directly Awarded — no bid placed, no connects">
                              Direct Award
                            </span>
                          ) : b.entryType === "Invitation" ? (
                            <div className="flex flex-col items-center gap-0.5">
                              <span className="bg-violet-100 px-2.5 py-1 rounded-md text-[11px] font-bold text-violet-700" title="Invitation — 0 base connects (a Boost still consumes)">
                                Invited
                              </span>
                              {b.boostUsed && (b.boostConnects || 0) > 0 && !b.connectsRefunded && (
                                <span className="text-[10px] font-bold text-emerald-600" title="Boost connects consumed on this invitation">
                                  {b.boostConnects} boost
                                </span>
                              )}
                            </div>
                          ) : (
                            <div className="flex flex-col items-center gap-0.5">
                              <span
                                className="bg-slate-100 px-2.5 py-1 rounded-md text-xs font-bold text-[#03488A]"
                                title={b.boostUsed ? `Base ${b.connectsUsed ?? 0} + Boost ${b.boostConnects ?? 0}` : "Total connects"}
                              >
                                {b.totalConnects ?? ((b.connectsUsed ?? 0) + (b.boostUsed ? b.boostConnects ?? 0 : 0))}
                              </span>
                              {Number(b.connectCost) > 0 && (
                                <span className="text-[10px] font-semibold text-rose-600" title={`Connect cost @ $${b.connectRate || 0}/connect`}>
                                  ${Number(b.connectCost).toLocaleString()}
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {isEdit ? (
                            <input
                              type="number"
                              className="w-24 px-2 py-1.5 border border-slate-200 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-[#00AAF1]/40"
                              value={editForm.bidAmount}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  bidAmount: e.target.value,
                                })
                              }
                            />
                          ) : b.bidAmount ? (
                            <span className="text-sm font-bold text-slate-800">
                              ${Number(b.bidAmount).toLocaleString()}
                            </span>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {isEdit ? (
                            <select
                              className="px-2 py-1.5 border border-slate-200 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-[#00AAF1]/40 bg-white"
                              value={editForm.bidType}
                              onChange={(e) =>
                                setEditForm({ ...editForm, bidType: e.target.value })
                              }
                            >
                              <option value="Fixed">Fixed</option>
                              <option value="Hourly">Hourly</option>
                            </select>
                          ) : (
                            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                              {b.bidType || "Fixed"}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-center">
                          {isEdit ? (
                            <div className="flex flex-col items-center gap-1.5">
                              <button
                                type="button"
                                disabled={editForm.entryType === DIRECT_TYPE}
                                onClick={() =>
                                  setEditForm({
                                    ...editForm,
                                    boostUsed: !editForm.boostUsed,
                                  })
                                }
                                className="grid place-items-center mx-auto disabled:opacity-30 disabled:cursor-not-allowed"
                                title={editForm.entryType === DIRECT_TYPE ? "Directly Awarded jobs need no Boost" : editForm.boostUsed ? "Boosted" : "Organic"}
                              >
                                <Zap
                                  size={18}
                                  className={
                                    editForm.boostUsed
                                      ? "text-emerald-500 fill-emerald-500"
                                      : "text-slate-300"
                                  }
                                />
                              </button>
                              {editForm.boostUsed && (
                                <div className="flex flex-col gap-1.5 items-stretch min-w-[130px]">
                                  <input
                                    type="number"
                                    min={0}
                                    placeholder="Boost connects"
                                    className="w-full px-2 py-1 border border-slate-200 rounded-md text-[11px] focus:outline-none focus:ring-2 focus:ring-[#00AAF1]/40"
                                    value={editForm.boostConnects || ""}
                                    onChange={(e) =>
                                      setEditForm({ ...editForm, boostConnects: Number(e.target.value) || 0 })
                                    }
                                  />
                                  <select
                                    className="w-full px-2 py-1 border border-slate-200 rounded-md text-[11px] bg-white focus:outline-none focus:ring-2 focus:ring-[#00AAF1]/40"
                                    value={editForm.boostPosition || 1}
                                    onChange={(e) =>
                                      setEditForm({ ...editForm, boostPosition: Number(e.target.value) })
                                    }
                                  >
                                    <option value={1}>1st Position</option>
                                    <option value={2}>2nd Position</option>
                                    <option value={3}>3rd Position</option>
                                    <option value={4}>4th Position</option>
                                  </select>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="flex flex-col items-center gap-1">
                              <Zap
                                size={18}
                                className={`${
                                  b.boostUsed
                                    ? "text-emerald-500 fill-emerald-500"
                                    : "text-slate-300"
                                }`}
                              />
                              {b.boostUsed && (
                                <div className="flex flex-col items-center gap-0.5 leading-none">
                                  {b.boostConnects > 0 && (
                                    <span className="text-[10px] font-bold text-emerald-600 whitespace-nowrap">
                                      +{b.boostConnects} conn
                                    </span>
                                  )}
                                  {b.boostPosition ? (
                                    <span className="text-[10px] font-semibold text-slate-500 whitespace-nowrap">
                                      Pos {b.boostPosition}
                                    </span>
                                  ) : null}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {isEdit ? (
                            <div className="space-y-1.5">
                              <select
                                className="px-2 py-1.5 border border-slate-200 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-[#00AAF1]/40 bg-white w-full"
                                value={editForm.bidStatus}
                                onChange={(e) =>
                                  setEditForm({
                                    ...editForm,
                                    bidStatus: e.target.value,
                                  })
                                }
                              >
                                {statusOptionsCfg.map((s) => (
                                  <option key={s} value={s}>
                                    {s}
                                  </option>
                                ))}
                              </select>
                              {editForm.bidStatus === DELETED_STATUS && (
                                <label className="flex items-center gap-1.5 text-[10px] font-semibold text-slate-600 cursor-pointer bg-zinc-50 border border-zinc-200 rounded-md px-2 py-1">
                                  <input
                                    type="checkbox"
                                    checked={!!editForm.connectsRefunded}
                                    onChange={(e) =>
                                      setEditForm({
                                        ...editForm,
                                        connectsRefunded: e.target.checked,
                                      })
                                    }
                                    className="rounded border-slate-300 text-[#03488A] focus:ring-[#00AAF1]/40"
                                  />
                                  Connects recovered by Upwork?
                                </label>
                              )}
                              {editForm.bidStatus === "In Negotiation" && !editForm.hasContact && (
                                <input
                                  placeholder="Client name → creates contact"
                                  value={editForm.clientName || ""}
                                  onChange={(e) =>
                                    setEditForm({ ...editForm, clientName: e.target.value })
                                  }
                                  className="w-full px-2 py-1.5 border border-amber-300 bg-amber-50 rounded-md text-[11px] focus:outline-none focus:ring-2 focus:ring-amber-300"
                                />
                              )}
                            </div>
                          ) : (
                            <StatusPill status={b.bidStatus} />
                          )}
                        </td>
                        {/* Project column hidden for now
                        <td className="px-6 py-4">
  {isEdit ? (
    <select
      className="px-2 py-1.5 border border-slate-200 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-[#00AAF1]/40"
      value={editForm.projectRef}
      onChange={(e) => setEditForm({ ...editForm, projectRef: e.target.value })}
    >
      <option value="">— None —</option>
      {projects.map((p) => (
        <option key={p._id} value={p._id}>{p.name}</option>
      ))}
    </select>
  ) : (
    <span className="text-xs text-slate-600">
      {b.projectRef?.name || (typeof b.projectRef === "string"
        ? projects.find(p => p._id === b.projectRef)?.name
        : null) || <span className="text-slate-400">—</span>}
    </span>
  )}
</td>
                        */}
                        <td className="px-6 py-4 text-sm text-slate-500" title={b.submittedAt ? `Submitted ${new Date(b.submittedAt).toLocaleString()}` : undefined}>
                          {new Date(
                            b.submittedAt || b.createdAt || b.submissionTimestamp || ""
                          ).toLocaleDateString(undefined, {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </td>

                        <td className="px-6 py-4 sticky right-0 z-10 bg-white shadow-[-6px_0_8px_-4px_rgba(15,23,42,0.08)] group-hover:bg-slate-50/90">
                          <div className="flex gap-1 justify-end">
                            {isEdit ? (
                              <>
                                <button
                                  onClick={() => saveEdit(b)}
                                  className={`${brandGradient} text-white p-1.5 rounded-md hover:opacity-90`}
                                  title="Save"
                                >
                                  <Save size={14} />
                                </button>
                                <button
                                  onClick={cancelEdit}
                                  className="p-1.5 rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50"
                                  title="Cancel"
                                >
                                  <X size={14} />
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => startEdit(b)}
                                  className="p-1.5 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-[#00AAF1]/40 hover:text-[#03488A] transition"
                                  title="Edit inline"
                                >
                                  <Edit2 size={14} />
                                </button>
                           <button
                                  onClick={async () => {
                                    if (!b.jobLink) return toast.error("No job link on this bid");

                                    if (b.jobLink && !b.jobLink.includes("~02")) {
                                      return toast.error("Invalid Upwork URL");
                                    }

                                    setAiModal({ open: true, job: null, ai: null, url: b.jobLink });
                                    setAnalyzing(true);

                                    try {
                                      const result = await checkJobUrl(b.jobLink);

                                      if (result.status === "matched") {
                                        setAiModal({ open: true, job: result.job, ai: result.ai, url: b.jobLink });
                                        setAnalyzing(false);
                                        return;
                                      }

                                      if (result.status === "invalid_url") {
                                        setAiModal({ open: false, job: null, ai: null });
                                        setAnalyzing(false);
                                        return toast.error("Invalid Upwork URL");
                                      }

                                      toast.success("Analyzing this job with AI…");
                                      const analyzed = await analyzeJob(b.jobLink);

                                      if (analyzed.status === "completed" && analyzed.job) {
                                        setAiModal({ open: true, job: analyzed.job, ai: analyzed.ai, url: b.jobLink });
                                      } else {
                                        toast(analyzed.message || "Analysis queued — check back shortly");
                                        setAiModal({ open: false, job: null, ai: null });
                                      }
                                    } catch {
                                      setAiModal({ open: false, job: null, ai: null });
                                    } finally {
                                      setAnalyzing(false);
                                    }
                                  }}
                                  className="p-1.5 rounded-md border border-slate-200 text-violet-600 hover:bg-violet-50 hover:border-violet-200 transition"
                                  title="View AI Analysis"
                                >
                                  <Sparkles size={14} />
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedBid(b);
                                    setDrawerOpen(true);
                                  }}
                                  className="p-1.5 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-[#00AAF1]/40 hover:text-[#03488A] transition"
                                  title="View details / notes"
                                >
                                  <Eye size={14} />
                                </button>
                                {WON_STATUSES.includes(b.bidStatus) && !b.projectRef && (
                                  <button
                                    onClick={() =>
                                      navigate("/projects", { state: { createFromBid: b._id } })
                                    }
                                    className="p-1.5 rounded-md border border-slate-200 text-emerald-600 hover:bg-emerald-50 hover:border-emerald-200 transition"
                                    title="Create project from this won bid"
                                  >
                                    <FolderPlus size={14} />
                                  </button>
                                )}
                                <button
                                  onClick={() => handleDelete(b._id)}
                                  className="p-1.5 rounded-md border border-slate-200 text-red-500 hover:bg-red-50 hover:border-red-200 transition"
                                  title="Delete"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                  {!drafts.length && filtered.length === 0 && (
                    <tr>
                      <td
                        colSpan={9}
                        className="text-center py-16 text-slate-400"
                      >
                        <div className="flex flex-col items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-slate-100 grid place-items-center">
                            <Plus size={22} className="text-slate-400" />
                          </div>
                          <div className="text-sm">
                            No bids yet. Click{" "}
                            <span className="font-semibold text-[#03488A]">
                              "New Bid"
                            </span>{" "}
                            to create one.
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex items-center justify-between px-2">
            <span className="text-xs text-slate-500 font-medium">
              Showing {filtered.length} of {bids.length} bids
            </span>
          </div>
        </div>
      </div>

      <BidQuickViewDrawer
        bid={selectedBid}
        isOpen={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedBid(null);
        }}
        onUpdate={() => {
          fetchMyBids();
          fetchAccounts();
        }}
      />
      <JobAnalysisModal
        open={aiModal.open}
        onClose={() => setAiModal({ open: false, job: null, ai: null })}
        job={aiModal.job}
        ai={aiModal.ai}
        onAnalyze={aiModal.url ? () => triggerAnalyze(aiModal.url!) : undefined}
        analyzing={analyzing}
      />
    </div>
  );
};

export default BidsPage;
